import { Box, Grid, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { convertToFormat } from 'app/helpers/dateHelper';
import { IPlacementContractVersion } from 'store/redux-store/placement-candidate-contract-doc-editor/types';
import { PreviewScreen } from '../customComponents';
import { GenericDialog, IGenericDialogButton } from 'app/components/Alerts/GenericDialog';

const useStyles = makeStyles()(theme => ({
  dialogContent: {
    paddingTop: `${theme.spacing(2)} !important`,
    paddingBottom: `${theme.spacing(2)} !important`,
    backgroundColor: theme.palette.common.white,
  },
  bodyRow: {
    display: 'flex',
    flex: 1,
    minHeight: 420,
    maxHeight: 'min(70vh, 640px)',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    overflow: 'hidden',
  },
  listPanel: {
    width: 280,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflow: 'auto',
    backgroundColor: theme.palette.grey[50],
  },
  previewPanel: {
    flex: 1,
    minWidth: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.grey[100],
  },
  titleLeadingIcon: {
    color: theme.palette.common.white,
    fontSize: 28,
  },
}));

/** History dialog shows prior snapshot (`templateHtmlOld`); fall back if API omits it. */
function resolveVersionPreviewHtml(v: IPlacementContractVersion | null): string {
  if (!v) return '';
  const oldHtml = v.templateHtmlOld?.trim();
  if (oldHtml) return oldHtml;
  const html = v.templateHtml?.trim();
  if (html) return html;
  const text = v.templateText?.trim() ?? '';
  if (text.startsWith('<')) return text;
  return '';
}

export const PlacementContractVersionHistoryModal: React.FC<{
  open: boolean;
  versions: IPlacementContractVersion[];
  onClose: () => void;
  onSelect: (html: string) => void;
}> = ({ open, versions, onClose, onSelect }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [selected, setSelected] = useState<IPlacementContractVersion | null>(null);

  const orderedVersions = useMemo(() => [...versions].reverse(), [versions]);

  useEffect(() => {
    if (!open) {
      setSelected(null);
      return;
    }
    setSelected(orderedVersions[0] ?? null);
  }, [open, orderedVersions]);

  const previewHtml = resolveVersionPreviewHtml(selected);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSelect = useCallback(() => {
    const html = resolveVersionPreviewHtml(selected);
    if (html !== '') onSelect(html);
    onClose();
  }, [selected, onSelect, onClose]);

  const dialogActions = useMemo((): IGenericDialogButton[] => {
    return [
      {
        text: t('templateEditor.placement.versionHistory.close').toUpperCase(),
        variant: 'contained',
        color: 'tertiary',
        onClick: handleClose,
      },
      {
        text: t('templateEditor.placement.versionHistory.select').toUpperCase(),
        variant: 'contained',
        color: 'primary',
        disabled: !selected || previewHtml === '',
        onClick: handleSelect,
      },
    ];
  }, [t, handleClose, handleSelect, selected, previewHtml]);

  return (
    <GenericDialog
      open={open}
      fullWidth
      maxWidth="lg"
      variant="blue"
      draggable
      onClose={handleClose}
      aria-labelledby="contract-version-history-title"
      dialogTitleProps={{
        text: t('templateEditor.placement.versionHistory.title'),
        leadingIcon: <HistoryOutlinedIcon className={classes.titleLeadingIcon} />,
        closeButton: true,
        expandable: true,
        ellipseText: true,
      }}
      dialogContentProps={{
        classes: { root: classes.dialogContent },
      }}
      dialogActions={dialogActions}
    >
      <Grid container>
        <Grid item xs={12}>
          <Box className={classes.bodyRow}>
            <List className={classes.listPanel} dense disablePadding>
              {orderedVersions.map((v, index) => (
                <ListItemButton
                  key={v.id}
                  selected={selected?.id === v.id}
                  onClick={() => setSelected(v)}
                  alignItems="flex-start"
                >
                  <ListItemText
                    primary={t('templateEditor.placement.versionHistory.versionLabel', {
                      id: orderedVersions.length - index,
                    })}
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {v.updatedByName}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {convertToFormat(v.dateEntered, 'MM/DD/YYYY h:mm A', true)}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
            <Box className={classes.previewPanel}>
              {previewHtml ? (
                <PreviewScreen document={previewHtml} htmlVersion disablePointerEvents={false} />
              ) : (
                <Box sx={{ p: 3 }}>
                  <Typography color="text.secondary">
                    {t('templateEditor.placement.versionHistory.emptyPreview')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </GenericDialog>
  );
};
